import { PatternsErrorFactory } from './PatternsErrorsFactory';
import { IGroupConfig, IBlockConfig } from './interfaces/patternsConfig.interface';
import { IParsedHtml } from './interfaces/parsedHtml';
import { ContentValidatorHelper } from '../../ContentValidatorHelper';
import { IErrorDetail } from '../../interfaces/validation.interface';
import { PatternsConstraints } from './PatternsConstraints';
import { PatternsReader } from './PatternsReader';

export class PatternsConstraintsGroup {
  private errorFactory: PatternsErrorFactory;
  config: IGroupConfig;
  constraints: PatternsConstraints;

  nodes: IParsedHtml[];

  currentNode: IParsedHtml | null = null;
  currentNodeIndex = 0;

  currentConfigIndex = 0;

  currentBlockConfig: IBlockConfig | IGroupConfig | undefined = undefined;
  previousBlockConfig: IBlockConfig | IGroupConfig | undefined = undefined;
  nextBlockConfig: IBlockConfig | IGroupConfig | undefined = undefined;

  constructor(
    nodes: IParsedHtml[],
    config: IGroupConfig,
    constraints: PatternsConstraints,
  ) {
    this.nodes = nodes;
    this.config = config;
    this.constraints = constraints;
    this.errorFactory = new PatternsErrorFactory(this, this.constraints);
  }

  validate (): {
    validation: IErrorDetail[],
    hasNotExistsError: boolean,
    lastNodeIndex: number,
    failedAtFirst: boolean,
    } {
    let _hasNotExistsError = false;
    let isToStop = false;
    let failedAtFirst = false;
    const validations: IErrorDetail[] = [];

    do {
      const { finished, hasNotExistsError, validationDetails } = this.validateNextConfig();
      isToStop = hasNotExistsError || finished;
      _hasNotExistsError = hasNotExistsError;

      if (_hasNotExistsError && (this.currentConfigIndex - 1) === 0) {
        failedAtFirst = true;
      }

      if (validationDetails) {
        validations.push(...validationDetails);
      }
    } while (!isToStop);

    return {
      validation: validations,
      hasNotExistsError: _hasNotExistsError,
      lastNodeIndex: this.currentNodeIndex,
      failedAtFirst,
    };
  }

  private validateNextConfig() {
    const validationDetails = ContentValidatorHelper.createErrorDetails();
    let hasNotExistsError = false;

    this.setBlockConfigs();

    if (this.isBlock(this.currentBlockConfig!)) {
      hasNotExistsError = this.runBlockValidation(this.currentBlockConfig!, validationDetails);
    } else {
      hasNotExistsError = this.runGroupValidation(this.currentBlockConfig!, validationDetails);
    }

    this.currentConfigIndex += 1;
    this.currentNodeIndex += 1;

    return {
      hasNotExistsError,
      validationDetails,
      finished: !this.hasNextConfig(),
    };
  }

  private runBlockValidation(blockConf: IBlockConfig, validationDetails: IErrorDetail[]) {
    let hasNotExistsError = false;

    const nodeGroup = this.getNodeGroupIfMatched(blockConf, this.currentNodeIndex);
    this.currentNode = nodeGroup;

    if (nodeGroup) {
      const lengthMatched = this.isLengthInside(blockConf, nodeGroup!);
      if (!lengthMatched) {
        validationDetails.push(
          this.errorFactory.createLengthIsIncorrect(),
        );
      }
    } else {
      hasNotExistsError = true;

      if (this.isOnStart()) {
        validationDetails.push(this.errorFactory.createNotExistsStart());
      } else {
        validationDetails.push(this.errorFactory.createNotExists());
      }
    }

    return hasNotExistsError;
  }

  private runGroupValidation(
    groupConf: IGroupConfig,
    validationDetails: IErrorDetail[],
  ) {
    let repeats = 0;
    let hasNotExistsError = false;
    let isToRepeat = true;

    do {
      if (!groupConf.repeats) isToRepeat = false;
      if (this.currentNodeIndex >= this.nodes.length - 1) isToRepeat = false;

      const groupValidator = new PatternsConstraintsGroup(
        PatternsReader.getNodesFromIndex(this.nodes, this.currentNodeIndex),
        groupConf,
        this.constraints,
      );

      const {
        lastNodeIndex,
        validation,
        failedAtFirst,
        hasNotExistsError: _hasNotExistsError,
      } = groupValidator.validate();

      if (_hasNotExistsError) {
        isToRepeat = false;

        if (
          (groupConf?.optional && !failedAtFirst) ||
          (!groupConf?.optional && repeats === 0) ||
          (!groupConf?.optional && repeats > 0 && !failedAtFirst)
        ) {
          validationDetails.push(...validation);
          hasNotExistsError = true;
        }
      } else {
        validationDetails.push(...validation);
        this.currentNodeIndex += lastNodeIndex;
      }

      repeats += 1;
    } while (isToRepeat);

    return hasNotExistsError;
  }

  private setBlockConfigs() {
    this.previousBlockConfig = this.config.blocks[this.currentConfigIndex - 1];
    this.currentBlockConfig = this.config.blocks[this.currentConfigIndex];
    this.nextBlockConfig = this.config.blocks[this.currentConfigIndex + 1];
  }

  private isBlock(blockOrGroup: IGroupConfig | IBlockConfig): blockOrGroup is IBlockConfig {
    if ((blockOrGroup as IBlockConfig).tag) {
      return true;
    }
    return false;
  }

  private getNodeGroupIfMatched(config: IBlockConfig, nodeIndex: number): IParsedHtml | null {
    const block = this.nodes[nodeIndex];

    if (
      block &&
      Array.isArray(config.tag)
        ? config.tag.includes(block?.type)
        : block?.type === config.tag
    ) {
      return block;
    }

    return null;
  }

  private isLengthInside(config: IBlockConfig, nodeGroup: IParsedHtml): boolean {
    if (typeof config.amount === 'number') {
      return nodeGroup.amount === config.amount;
    }
    return nodeGroup.amount >= config.amount[0] && nodeGroup.amount <= config.amount[1];
  }

  private isOnStart() {
    return !this.previousBlockConfig;
  }

  private hasNextConfig() {
    return !!this.nextBlockConfig;
  }
}
